import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronLeft, Heart, Shield, Users, Sparkles, Check, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Step Components
import RecoveryBasicsStep from "./onboarding/RecoveryBasicsStep";
import RecoveryImportanceStep from "./onboarding/RecoveryImportanceStep";
import CoreValuesStep from "./onboarding/CoreValuesStep";
import RelationshipIntentStep from "./onboarding/RelationshipIntentStep";
import BoundariesPacingStep from "./onboarding/BoundariesPacingStep";
import SpiritualityStep from "./onboarding/SpiritualityStep";
import SponsorSupportStep from "./onboarding/SponsorSupportStep";
import LifestyleStep from "./onboarding/LifestyleStep";
import SubstancesStep from "./onboarding/SubstancesStep";
import PreferencesStep from "./onboarding/PreferencesStep";
import ProfilePromptsStep from "./onboarding/ProfilePromptsStep";
import PhotosStep from "./onboarding/PhotosStep";

const STEPS = [
  { id: "welcome", title: "Welcome", theme: null, component: null },
  { id: "recovery_basics", title: "Recovery Journey", theme: "Honesty", themeDescription: "Being truthful about your story", component: RecoveryBasicsStep },
  { id: "recovery_importance", title: "Your Priorities", theme: "Hope", themeDescription: "Believing in meaningful connection", component: RecoveryImportanceStep },
  { id: "spirituality", title: "Spirituality", theme: "Faith", themeDescription: "Your beliefs and higher power", component: SpiritualityStep },
  { id: "sponsor_support", title: "Sponsor & Support", theme: "Courage", themeDescription: "Being vulnerable about your support", component: SponsorSupportStep },
  { id: "core_values", title: "Core Values", theme: "Integrity", themeDescription: "What you stand for", component: CoreValuesStep },
  { id: "relationship_intent", title: "What You're Looking For", theme: "Willingness", themeDescription: "Opening yourself to love", component: RelationshipIntentStep },
  { id: "boundaries_pacing", title: "Boundaries & Pacing", theme: "Humility", themeDescription: "Knowing and honoring your limits", component: BoundariesPacingStep },
  { id: "lifestyle", title: "Your Lifestyle", theme: "Love", themeDescription: "How you care for yourself", component: LifestyleStep },
  { id: "substances", title: "Lifestyle Choices", theme: "Justice", themeDescription: "Being honest about your choices", component: SubstancesStep },
  { id: "preferences", title: "Match Preferences", theme: "Perseverance", themeDescription: "Knowing what works for you", component: PreferencesStep },
  { id: "profile_prompts", title: "Your Story", theme: "Awareness", themeDescription: "Reflecting on your journey", component: ProfilePromptsStep },
  { id: "photos", title: "Photos", theme: "Service", themeDescription: "Putting yourself out there", component: PhotosStep },
  { id: "complete", title: "All Set!", theme: null, component: null },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [stepData, setStepData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await api.getOnboardingStatus();
      const { is_complete, completed_steps, current_step } = response.data;

      if (is_complete) {
        navigate("/matches");
        return;
      }

      // Find the current step index
      if (current_step) {
        const stepIndex = STEPS.findIndex(s => s.id === current_step);
        if (stepIndex > 0) {
          setCurrentStep(stepIndex);
        }
      }
    } catch (error) {
      console.error("Failed to check onboarding status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async (data) => {
    const step = STEPS[currentStep];
    
    if (data) {
      setStepData(prev => ({ ...prev, [step.id]: data }));
    }

    // If this is the last real step (photos), go to complete
    if (currentStep === STEPS.length - 2) {
      setCurrentStep(STEPS.length - 1);
      return;
    }

    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      await api.completeOnboarding();
      await refreshUser();
      navigate("/matches");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete onboarding");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1C3A7A] to-[#0f2347] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  const CurrentStepComponent = STEPS[currentStep]?.component;
  const progress = ((currentStep) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1C3A7A] to-[#0f2347] flex flex-col">
      {/* Progress Bar */}
      {currentStep > 0 && currentStep < STEPS.length - 1 && (
        <div className="fixed top-0 left-0 right-0 h-1 bg-white/20 z-50">
          <motion.div
            className="h-full bg-[#FAB710]"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      )}

      {/* Header */}
      {currentStep > 0 && currentStep < STEPS.length - 1 && (
        <div className="px-4 pt-6 pb-2">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <button
              onClick={handleBack}
              className="p-2 text-white/70 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="text-center">
              {STEPS[currentStep].theme && (
                <div className="mb-1">
                  <span className="inline-flex items-center gap-1.5 bg-[#FAB710] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    Step {currentStep} of 12: {STEPS[currentStep].theme}
                  </span>
                </div>
              )}
              <h2 className="text-white font-semibold text-lg">{STEPS[currentStep].title}</h2>
              {STEPS[currentStep].themeDescription && (
                <p className="text-white/60 text-xs mt-0.5 italic">{STEPS[currentStep].themeDescription}</p>
              )}
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="p-2 text-white/70 hover:text-white transition-colors"
              title="Exit & Logout"
              data-testid="onboarding-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-lg"
          >
            {/* Welcome Screen */}
            {currentStep === 0 && (
              <WelcomeScreen onNext={() => handleNext()} onLogout={() => { logout(); navigate('/'); }} />
            )}

            {/* Step Components */}
            {CurrentStepComponent && (
              <CurrentStepComponent
                data={stepData[STEPS[currentStep].id]}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}

            {/* Complete Screen */}
            {currentStep === STEPS.length - 1 && (
              <CompleteScreen onComplete={handleComplete} loading={submitting} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Welcome Screen Component
const WelcomeScreen = ({ onNext, onLogout }) => {
  return (
    <div className="text-center">
      {/* Logo */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">Recovery</span>
          <span className="text-[#FAB710]">Match</span>
        </h1>
        <p className="text-white/80 text-lg">Healthy connections for people committed to recovery</p>
      </div>

      {/* Features */}
      <div className="space-y-4 mb-10">
        <FeatureItem
          icon={<Shield className="w-6 h-6" />}
          title="Values-First Matching"
          description="We match based on recovery alignment, values, and intentions—not just looks"
        />
        <FeatureItem
          icon={<Users className="w-6 h-6" />}
          title="Curated Daily Matches"
          description="Receive 3-5 thoughtfully selected matches each day. No endless swiping."
        />
        <FeatureItem
          icon={<Heart className="w-6 h-6" />}
          title="Built for Recovery"
          description="A safe space designed by and for people in recovery"
        />
      </div>

      {/* CTA */}
      <Button
        onClick={onNext}
        className="w-full h-14 bg-[#FAB710] hover:bg-[#e8a50f] text-[#1C3A7A] text-lg font-semibold rounded-xl"
        data-testid="onboarding-get-started-btn"
      >
        Get Started
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>

      <p className="text-white/50 text-sm mt-4">
        Takes about 5-7 minutes to complete
      </p>

      {/* Logout option */}
      <button
        onClick={onLogout}
        className="mt-6 text-white/40 hover:text-white/70 text-sm flex items-center justify-center gap-1 mx-auto transition-colors"
        data-testid="onboarding-welcome-logout-btn"
      >
        <LogOut className="w-4 h-4" />
        Sign out
      </button>
    </div>
  );
};

const FeatureItem = ({ icon, title, description }) => (
  <div className="flex items-start gap-4 text-left bg-white/10 rounded-xl p-4">
    <div className="w-12 h-12 bg-[#FAB710]/20 rounded-full flex items-center justify-center text-[#FAB710] flex-shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="text-white font-semibold mb-1">{title}</h3>
      <p className="text-white/70 text-sm">{description}</p>
    </div>
  </div>
);

// Complete Screen Component
const CompleteScreen = ({ onComplete, loading }) => {
  return (
    <div className="text-center">
      {/* Success Animation */}
      <div className="mb-8">
        <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30">
          <Check className="w-12 h-12 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">You&apos;re All Set!</h1>
        <p className="text-white/80">Your profile is complete. Your first matches are being prepared.</p>
      </div>

      {/* What's Next */}
      <div className="bg-white/10 rounded-xl p-6 mb-8 text-left">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#FAB710]" />
          What happens next?
        </h3>
        <ul className="space-y-3 text-white/80">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-[#FAB710] text-[#1C3A7A] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
            <span>You&apos;ll receive 3-5 curated matches based on your compatibility</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-[#FAB710] text-[#1C3A7A] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
            <span>Review each match&apos;s profile and compatibility breakdown</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-[#FAB710] text-[#1C3A7A] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
            <span>Express interest to start a guided conversation</span>
          </li>
        </ul>
      </div>

      <Button
        onClick={onComplete}
        disabled={loading}
        className="w-full h-14 bg-[#FAB710] hover:bg-[#e8a50f] text-[#1C3A7A] text-lg font-semibold rounded-xl"
      >
        {loading ? (
          <div className="w-6 h-6 border-2 border-[#1C3A7A]/30 border-t-[#1C3A7A] rounded-full animate-spin" />
        ) : (
          <>
            See My Matches
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default OnboardingPage;
